import React, { useState, useEffect } from 'react';
import '../styles/index.css';
import { useSignup } from '../hooks/useSignup';
import { useLogin } from '../hooks/useLogin';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import signupBG from '../images/signupBackground.png';

const Signup = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [galleryId, setGalleryId] = useState('');
  const [galleries, setGalleries] = useState([]);
  const [isLoadingGalleries, setIsLoadingGalleries] = useState(true);
  const [googleError, setGoogleError] = useState(null);
  const { signup, error, isLoading } = useSignup();
  const { loginWithGoogle } = useLogin();
  const navigate = useNavigate();

  // Fetch galleries on component mount
  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await axiosInstance.get('/gallery');
        if (response.data && response.data.galleries) {
          setGalleries(response.data.galleries);
        }
      } catch (error) {
        console.error('Error fetching galleries:', error);
      } finally {
        setIsLoadingGalleries(false);
      }
    };

    fetchGalleries();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setConfirmPasswordError('');

    if (firstName.trim() === '' || lastName.trim() === '') {
      alert('Please enter both first name and last name.');
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setConfirmPasswordError('Password must be at least 8 characters.');
      return;
    }

    if (!galleryId) {
      alert('Please select a gallery. Artization is for artists and curators with gallery access.');
      return;
    }

    const user = await signup(firstName, lastName, email, password, galleryId);

  if (user) {
    if (user.status === 'pending' || user.status === 'disabled') {
      navigate('/welcome');
    } else if (user.permissions && user.permissions.length > 0) {
      navigate('/home');
    } else {
      navigate('/welcome');
    }
  } else {
    alert('Signup failed. Please try again.');
    document.getElementById('firstName').focus();
  }


  };

  return (
    <div className="relative flex items-center justify-center min-h-screen w-full">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${signupBG})` }}
        aria-hidden
      />
      {/* Form panel */}
      <div className="relative z-10 grid w-auto grid-cols-1 gap-0 px-8 py-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-white/20">
        <h1 className="text-2xl font-bold text-center">Create Account</h1>
        <div className="p-2 text-center">
              <span className="text-blue-400">
                <a href="/login">Have an Account? Log in.</a>
              </span>
            </div>
        <div className="my-0 w-96 max-w-full">
          <form className="login" onSubmit={handleSubmit}>
            <div className="mb-2 grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="block dark">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  onChange={(e) => setFirstName(e.target.value)}
                  value={firstName}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded bg-logo focus:outline-none focus:border-emerald-300"
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block dark">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  onChange={(e) => setLastName(e.target.value)}
                  value={lastName}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded bg-logo focus:outline-none focus:border-emerald-300"
                  required
                />
              </div>
            </div>
            <div className="mb-2">
              <label htmlFor="email" className="block dark">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded bg-logo focus:outline-none focus:border-emerald-300"
                required
              />
            </div>
            <div className="mb-2">
              <label htmlFor="password" className="block dark">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (confirmPassword) setConfirmPasswordError('');
                }}
                value={password}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded bg-logo focus:outline-none focus:border-emerald-300"
                required
                minLength={8}
              />
            </div>
            <div className="mb-2">
              <label htmlFor="confirmPassword" className="block dark">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setConfirmPasswordError('');
                }}
                value={confirmPassword}
                className={`w-full px-2.5 py-1.5 text-sm border rounded bg-logo focus:outline-none focus:border-emerald-300 ${confirmPasswordError ? 'border-red-500' : 'border-gray-300'}`}
                required
                minLength={8}
              />
              {confirmPasswordError && (
                <p className="mt-1 text-sm text-red-500">{confirmPasswordError}</p>
              )}
            </div>
            <div className="mb-2">
              <label htmlFor="galleryId" className="block dark">
                Gallery <span className="text-red-500">*</span>
              </label>
              {isLoadingGalleries ? (
                <div className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded bg-logo text-gray-500">
                  Loading galleries...
                </div>
              ) : (
                <select
                  id="galleryId"
                  name="galleryId"
                  onChange={(e) => setGalleryId(e.target.value)}
                  value={galleryId}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded bg-logo focus:outline-none focus:border-emerald-300"
                  required
                >
                  <option value="">Select a gallery</option>
                  {galleries.map((gallery) => (
                    <option key={gallery._id} value={gallery.galleryId}>
                      {gallery.displayName} ({gallery.galleryId})
                    </option>
                  ))}
                </select>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Artization provides analytics for artists and curators. Curator access is assigned by an administrator after signup.
              </p>
            </div>
            <button
              disabled={isLoading}
              type="submit"
              className="w-full py-2 font-semibold text-white bg-blue-600 rounded hover:bg-emerald-500 focus:outline-none focus:ring focus:ring-blue-300"
            >
              Sign Up
            </button>
            {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <div className="mt-4 mb-0 flex flex-col items-center gap-2">
              <span className="text-sm text-gray-600">Or continue with</span>
              <GoogleLogin
                onSuccess={(res) => {
                  setGoogleError(null);
                  if (res?.credential) loginWithGoogle(res.credential);
                }}
                onError={() => setGoogleError('Google sign-in was cancelled or failed.')}
                useOneTap={false}
                theme="outline"
                size="medium"
                text="signup_with"
                shape="rectangular"
              />
            </div>
            )}
            
            {(error || googleError) && <span className="block text-lg text-red-500 mt-2">{error || googleError}</span>}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
